"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { marketPageUrl } from "@/lib/source-url";
import { setNavigationSource } from "@/lib/recommendation-tracker";
import { BellIcon } from "./icons/BellIcon";
import { BellRingIcon } from "./icons/BellRingIcon";
import { authFetch } from "@/lib/authFetch";
import { useUserMe } from "@/contexts/UserMeContext";
import { useT } from "@/hooks/useT";
import LeaderAvatar from "./LeaderAvatar";
import GradientAvatar from "./GradientAvatar";
import {
  Copy,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Plus,
  Lock,
  Clock,
  Award,
  Star,
  Check,
  Eye,
  Target,
  DollarSign,
  Gift,
  ArrowRight,
  ArrowDownToLine,
  Loader2,
  Sparkles,
} from "lucide-react";
import { formatCents } from '@/lib/format-cents';
import { marketDisplayTitle } from "@/lib/markets/title";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data: {
    // Common routing
    marketSlug?: string;
    eventSlug?: string;
    outcome?: string;
    isMultiOutcome?: boolean;
    // copy_trade
    leaderName?: string;
    marketTitle?: string;
    marketImageUrl?: string;
    traderImageUrl?: string;
    traderWalletAddress?: string;
    side?: "BUY" | "SELL";
    size?: number;
    price?: number;
    notional?: number;
    won?: boolean;
    pnl?: number;
    isLimitOrder?: boolean;
    // 2026-05-26: Per-variant chip for multi-outcome PM markets. When set,
    // the dropdown row renders e.g. "BUY YES · 28°C" instead of just
    // "BUY YES" so the user can tell sub-markets apart inside a group.
    groupItemTitle?: string;
    // 2026-05-26: parent event headline. Used as the row headline when
    // groupItemTitle is present (avoids repeating the variant string —
    // it's already in the chip). Falls back to marketTitle otherwise.
    eventTitle?: string;
    // wallet_tracker
    walletAddress?: string;
    traderLabel?: string;
    traderProfileImageUrl?: string;
    marketIcon?: string;
    eventType?: "new" | "closed" | "updated";
    tradeAmount?: number;
    tradePrice?: number;
    calculatedSize?: number;
    calculatedValue?: number;
    // achievements
    achievementId?: string;
    [key: string]: any;
  };
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

interface NotificationsDropdownProps {
  unreadCount: number;
  onCountChange: (count: number) => void;
}

/* ──────────────────────────── Helpers ───────────────────────────── */

// Strip emoji + bold/anchor markup that the backend bakes into title/message
// strings. We render structurally on the client now, so the literals are
// noise (and clash with the flat icon style).
const EMOJI_RE = /[\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{27BF}\u{1F900}-\u{1F9FF}]/gu;
function stripEmojiAndTags(s: string): string {
  return s.replace(EMOJI_RE, "").replace(/<[^>]*>/g, "").replace(/\s{2,}/g, " ").trim();
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}

/** Smart link routing.
 *
 *  - wallet_tracker (Position New/Closed/Increased/Reduced) → trader profile,
 *    so the user can dig into the wallet they're following.
 *  - copy_trade / position_resolved → event page with the right outcome
 *    pre-selected.
 *  - everything else stays on its sensible destination (portfolio / settings
 *    / affiliate).
 */
function getNotificationLink(notification: Notification, username?: string | null): string | null {
  const { type, data } = notification;

  if (type === "wallet_tracker") {
    if (data?.walletAddress) return `/traders/${data.walletAddress}`;
    if (data?.traderLabel) return `/traders/${encodeURIComponent(data.traderLabel)}`;
  }

  // Copy-trade and position resolution → event page (outcome restored)
  if ((type === "copy_trade" || type === "position_resolved") && data?.marketSlug) {
    return marketPageUrl({
      marketSlug: data.marketSlug,
      eventSlug: data.eventSlug,
      outcome: data.outcome,
      source: "polymarket",
      isMultiOutcome: data.isMultiOutcome,
    });
  }

  // Generic market-bearing notifications (price alerts etc.)
  if (data?.marketSlug) {
    return marketPageUrl({
      marketSlug: data.marketSlug,
      eventSlug: data.eventSlug,
      outcome: data.outcome,
      source: "polymarket",
      isMultiOutcome: data.isMultiOutcome,
    });
  }

  if (type === "copy_trade" || type === "position_resolved") return "/portfolio";

  if (type === "achievement" || type === "achievement_unlocked") {
    return username ? `/profile/${username}?tab=achievements` : "/portfolio";
  }

  if (type === "low_balance" || type === "bridge_completed") return "/portfolio";
  if (type === "referral_earned") return "/affiliate";
  if (type === "funding_nudge") return "/portfolio?addFunds=1";

  return null;
}

/** Map notification type → flat icon + accent color (used when no avatar
 *  or market image is available, or as a small status pip overlaid on the
 *  thumbnail). */
function notificationVisual(n: Notification): { icon: React.ReactNode; tone: "emerald" | "red" | "violet" | "amber" | "blue" | "neutral" } {
  const t = n.type;
  const ev = n.data?.eventType;
  const side = n.data?.side;

  if (t === "wallet_tracker") {
    if (ev === "new") return { icon: <Plus className="w-3 h-3" strokeWidth={3} />, tone: "violet" };
    if (ev === "closed") return { icon: <Lock className="w-3 h-3" strokeWidth={2.5} />, tone: "neutral" };
    if (ev === "updated") {
      const isBuy = side === "BUY";
      return {
        icon: isBuy ? <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> : <TrendingDown className="w-3 h-3" strokeWidth={2.5} />,
        tone: isBuy ? "emerald" : "red",
      };
    }
    return { icon: <Eye className="w-3 h-3" strokeWidth={2.5} />, tone: "blue" };
  }
  if (t === "copy_trade") return { icon: <Copy className="w-3 h-3" strokeWidth={2.5} />, tone: "violet" };
  if (t === "limit_filled") return { icon: <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />, tone: "emerald" };
  if (t === "limit_expired" || t === "limit_cancelled") return { icon: <Clock className="w-3 h-3" strokeWidth={2.5} />, tone: "amber" };
  if (t === "redeem_complete") {
    // Tone follows partial-success flag: all redeemed → emerald
    // celebratory, any failures → amber so the user notices the partial
    // outcome without scanning the body copy.
    const totalFailed = Number(n.data?.totalFailed ?? 0);
    return {
      icon: <ArrowDownToLine className="w-3 h-3" strokeWidth={2.5} />,
      tone: totalFailed > 0 ? "amber" : "emerald",
    };
  }
  if (t === "deposit_wrapped") {
    // Auto-wrap completed (funds already tradeable). Emerald celebratory
    // — matches the redeem_complete + bridge_completed sister cases so a
    // wallet/funds family of notifications reads as one design language.
    return { icon: <ArrowDownToLine className="w-3 h-3" strokeWidth={2.5} />, tone: "emerald" };
  }
  if (t === "deposit_needs_approval") {
    // Funds arrived but the per-asset Onramp approval is missing —
    // requires one gasless signature. Amber so the user notices the
    // pending action without it shouting like a hard error.
    return { icon: <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />, tone: "amber" };
  }
  if (t === "position_resolved") {
    const won = n.data?.won;
    return { icon: won ? <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> : <XCircle className="w-3 h-3" strokeWidth={2.5} />, tone: won ? "emerald" : "red" };
  }
  if (
    t === "low_balance" ||
    t === "high_slippage" ||
    t === "copy_below_min" ||
    t === "copy_above_max" ||
    t === "insufficient_balance" ||
    t === "market_odds_too_high"
  ) return { icon: <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />, tone: "amber" };
  if (t === "achievement" || t === "achievement_unlocked") return { icon: <Award className="w-3 h-3" strokeWidth={2.5} />, tone: "violet" };
  if (t === "fren_points") return { icon: <Star className="w-3 h-3" strokeWidth={2.5} />, tone: "amber" };
  if (t === "price_alert") return { icon: <Target className="w-3 h-3" strokeWidth={2.5} />, tone: "amber" };
  if (t === "referral_earned") return { icon: <DollarSign className="w-3 h-3" strokeWidth={2.5} />, tone: "emerald" };
  if (t === "flowlink_claimed") return { icon: <Gift className="w-3 h-3" strokeWidth={2.5} />, tone: "violet" };
  if (t === "bridge_completed") return { icon: <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />, tone: "emerald" };
  // funding_nudge → "add funds" prompt; amber = pending action, not a success.
  if (t === "funding_nudge") return { icon: <DollarSign className="w-3 h-3" strokeWidth={2.5} />, tone: "amber" };
  return { icon: <Sparkles className="w-3 h-3" strokeWidth={2.5} />, tone: "neutral" };
}

const TONE_BG: Record<string, string> = {
  emerald: "bg-[#00FF9D]/20 text-[#00C97D] dark:text-[#00FF9D] ring-[#00FF9D]/25",
  red: "bg-[#FF1744]/20 text-[#FF1744] ring-[#FF1744]/25",
  violet: "bg-violet-500/10 text-violet-600 dark:text-violet-400 ring-violet-500/20",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/20",
  blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400 ring-blue-500/20",
  neutral: "bg-gray-500/10 text-gray-600 dark:text-gray-400 ring-gray-500/20",
};

/** Render a 36×36 thumbnail. Watchlist trades show the trader avatar;
 *  market-centric notifications show the market image; system events fall
 *  back to a tinted icon tile. A small status pip is overlaid in the
 *  bottom-right so the visual identity is clear at a glance. */
function NotificationThumb({ n }: { n: Notification }) {
  const { icon, tone } = notificationVisual(n);
  const pip = (
    <span
      className={`absolute -bottom-0.5 -right-0.5 flex items-center justify-center w-4 h-4 rounded-full ring-2 ring-white dark:ring-[#18181D] ${TONE_BG[tone]}`}
      aria-hidden
    >
      {icon}
    </span>
  );

  // 1. wallet_tracker → trader avatar (or gradient fallback)
  if (n.type === "wallet_tracker") {
    const url = n.data?.traderProfileImageUrl;
    const seed = n.data?.walletAddress || n.data?.traderLabel || n.id;
    return (
      <div className="relative flex-shrink-0 w-9 h-9">
        {url ? (
          <Image
            src={url}
            alt=""
            width={36}
            height={36}
            className="w-9 h-9 rounded-[5px] object-cover"
            unoptimized
          />
        ) : (
          <GradientAvatar seed={seed} size={36} className="!rounded-[5px]" />
        )}
        {pip}
      </div>
    );
  }

  // 2. Leader-centric copytrading events (auto-paused / auto-resumed,
  //    no market — they're about the leader, not a market). Prefer the
  //    leader avatar when the backend has hydrated it. Title carries the
  //    verb so the row reads "@leader · Copy Resumed".
  const titleLower = (n.title || '').toLowerCase();
  const isLeaderEvent =
    n.type === 'copy_trade' &&
    (titleLower.includes('auto-paused') || titleLower.includes('resumed'));
  const leaderImg = (n.data as any)?.leaderImage || (n.data as any)?.traderImageUrl;
  const leaderSeed = (n.data as any)?.traderWalletAddress || n.data?.leaderName;
  if (isLeaderEvent && (leaderImg || leaderSeed)) {
    return (
      <div className="relative flex-shrink-0 w-9 h-9">
        <LeaderAvatar
          src={leaderImg}
          address={leaderSeed ? String(leaderSeed) : null}
          size={36}
        />
        {pip}
      </div>
    );
  }

  // 3. copy_trade / market-bearing → market image (Polymarket S3) when available.
  //    For copy_trade rows we overlay a small leader avatar in the corner so
  //    the row reads "market + via @leader" at a glance instead of just a
  //    generic copy icon pip.
  const marketImg = n.data?.marketImageUrl || n.data?.marketIcon;
  if (marketImg) {
    const copyLeaderImg =
      n.type === 'copy_trade'
        ? ((n.data as any)?.leaderImage || (n.data as any)?.traderImageUrl)
        : null;
    const copyLeaderSeed =
      n.type === 'copy_trade'
        ? (n.data?.leaderName || n.data?.traderWalletAddress || n.id)
        : null;
    const showLeaderOverlay = n.type === 'copy_trade' && (copyLeaderImg || copyLeaderSeed);
    return (
      <div className="relative flex-shrink-0 w-9 h-9">
        <Image
          src={marketImg}
          alt=""
          width={36}
          height={36}
          className="w-9 h-9 rounded-[5px] object-cover bg-gray-100 dark:bg-[#0f0f12]"
          unoptimized
        />
        {showLeaderOverlay ? (
          <span
            className="absolute -bottom-1 -right-1 inline-flex rounded-full ring-2 ring-white dark:ring-[#18181D] overflow-hidden bg-gray-100 dark:bg-[#0f0f12]"
            aria-hidden
          >
            <LeaderAvatar
              src={copyLeaderImg}
              address={copyLeaderSeed ? String(copyLeaderSeed) : null}
              size={18}
            />
          </span>
        ) : (
          pip
        )}
      </div>
    );
  }

  // 3. fallback → tinted icon tile
  return (
    <div className={`relative flex-shrink-0 w-9 h-9 rounded-[5px] flex items-center justify-center ${TONE_BG[tone]}`}>
      <span className="scale-150">{icon}</span>
    </div>
  );
}

/** Acorta el título largo del market (mismo helper que portfolio/copytrading).
 *  Extrae el matchup ("Counter-Strike: A vs B") de los títulos de gamma con
 *  sufijos de torneo "(BO3) - … Group A". La notif no trae el array `outcomes`,
 *  así que tira del trim por título (vía eventTitle/marketTitle). */
function shortNotifTitle(d: Notification["data"]): string {
  if (!d?.marketTitle) return "";
  return marketDisplayTitle({
    isMultiOutcome: d.isMultiOutcome,
    eventTitle: d.eventTitle,
    eventSlug: d.eventSlug,
    marketQuestion: d.marketTitle,
    groupItemTitle: d.groupItemTitle,
  }, d.marketTitle);
}

/** Action verb for wallet_tracker rows, rendered in plain text */
function walletTrackerLabel(eventType?: string, side?: string): string {
  if (eventType === "new") return "New position";
  if (eventType === "closed") return "Closed position";
  if (eventType === "updated") return side === "BUY" ? "Increased" : "Reduced";
  return "Trade";
}

/** Short label for the "via @<leader>" chip — collapse address-shaped
 *  values (optionally with a "-<leaderId>" suffix) to `0x6982…A165` so a
 *  raw wallet doesn't overflow the badge. Mirrors PositionsSection /
 *  RecentActivitySection. */
function shortLeaderName(name?: string | null): string {
  if (!name) return "";
  const addr = name.match(/^(0x[0-9a-fA-F]{4})[0-9a-fA-F]+([0-9a-fA-F]{4})/);
  if (addr) return `${addr[1]}…${addr[2]}`;
  return name.length > 16 ? `${name.slice(0, 14)}…` : name;
}

/** "via @leader" badge — small leader avatar + handle inside a gray pill,
 *  same idiom as the /portfolio Activity rows. Truncates the handle so a
 *  long name shrinks instead of pushing the BUY/SELL pill off the row. */
function LeaderViaBadge({ name, image, address }: { name?: string | null; image?: string | null; address?: string | null }) {
  if (!name && !address) return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[4px] text-[10px] text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#25252B] min-w-0 max-w-full align-middle">
      <span className="opacity-60 flex-shrink-0">via</span>
      <LeaderAvatar src={image || undefined} address={address || name || null} size={12} />
      <span className="font-medium truncate min-w-0" title={name || undefined}>@{shortLeaderName(name)}</span>
    </span>
  );
}

/* ─────────────────────────── Component ──────────────────────────── */

// Dev-only preview rows (?mockNotifs=1) so the real dropdown can be eyeballed
// locally without depending on the account's actual notification history.
function buildMockNotifications(): Notification[] {
  const now = Date.now();
  const iso = (minAgo: number) => new Date(now - minAgo * 60_000).toISOString();
  const mk = (over: Partial<Notification> & { id: string; type: string; data: Notification["data"] }): Notification => ({
    title: "", message: "", read: false, readAt: null, createdAt: iso(over.id.length * 7), ...over,
  });
  return [
    mk({ id: "m1", type: "copy_trade", createdAt: iso(8), data: { leaderName: "YoshiValue", side: "SELL", size: 1, price: 0.999, notional: 0.97, marketTitle: "LoL: FlyQuest vs Team Liquid", outcome: "Team Liquid", marketSlug: "lol-flq-tl" } }),
    mk({ id: "m2", type: "copy_trade", createdAt: iso(14), data: { leaderName: "YoshiValue", side: "BUY", size: 2.1, price: 0.54, notional: 1.12, marketTitle: "LoL: FlyQuest vs Team Liquid", outcome: "Team Liquid", marketSlug: "lol-flq-tl" } }),
    mk({ id: "m3", type: "copy_trade", createdAt: iso(45), data: { leaderName: "keypi33", side: "BUY", size: 25, price: 0.05, notional: 1.25, marketTitle: "Price of Bitcoin above $66,000 on June 9", outcome: "Yes", marketSlug: "btc-66k-jun9" } }),
    mk({ id: "m4", type: "position_resolved", createdAt: iso(120), data: { won: true, pnl: 28.5, marketTitle: "NBA: Lakers vs Celtics", outcome: "Lakers", marketSlug: "nba-lal-bos" } }),
  ];
}

export default function NotificationsDropdown({ unreadCount, onCountChange }: NotificationsDropdownProps) {
  const isMockNotifs =
    process.env.NODE_ENV !== "production" &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("mockNotifs");
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user: userMe } = useUserMe();
  const t = useT();
  const currentUsername = userMe?.username || null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (isMockNotifs) {
      setNotifications(buildMockNotifications());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await authFetch("/api/notifications?limit=20");
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch {
      /* swallow */
    } finally {
      setLoading(false);
    }
  }, [isMockNotifs]);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      const res = await authFetch("/api/notifications/read-all", { method: "POST" });
      if (res.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        onCountChange(0);
      }
    } catch {
      /* swallow */
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await authFetch("/api/notifications/read", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: [notification.id] }),
        });
        setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)));
        onCountChange(Math.max(0, unreadCount - 1));
      } catch {
        /* swallow */
      }
    }
    setIsOpen(false);
  };

  return (
    <div ref={menuRef} className="auth-only relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#18181D] transition-colors relative"
        aria-label={t("notifications.title")}
      >
        {unreadCount > 0 ? (
          <BellRingIcon size={18} isAnimated className="text-gray-600 dark:text-gray-300" />
        ) : (
          <BellIcon size={18} className="text-gray-600 dark:text-gray-300" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-[5px]">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="fixed left-3 right-3 top-14 w-auto sm:absolute sm:left-auto sm:top-full sm:right-0 sm:mt-2 sm:w-[26rem] lg:w-[32rem] bg-gray-50 dark:bg-[#0f0f12] border border-gray-300 dark:border-[#33333A] ring-1 ring-black/5 dark:ring-white/5 rounded-[5px] shadow-xl shadow-black/10 dark:shadow-black/40 z-[60] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-[#25252B]">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{t("notifications.title")}</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markingAllRead}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
              >
                {markingAllRead ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {t("notifications.markAllRead")}
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[440px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <BellIcon size={28} className="mb-2 opacity-40" />
                <p className="text-sm">{t("notifications.empty")}</p>
              </div>
            ) : (
              <div className="p-2 space-y-2">
                {notifications.map((notification) => {
                  const link = getNotificationLink(notification, currentUsername);
                  const cleanTitle = stripEmojiAndTags(notification.title || "");

                  // Build a compact, structured body line per notification type
                  // instead of dumping the bake-text message. Falls back to a
                  // stripped version of `message` for legacy types.
                  let body: React.ReactNode = null;

                  if (notification.type === "wallet_tracker") {
                    const d = notification.data;
                    const action = walletTrackerLabel(d.eventType, d.side);
                    const outcomeColor =
                      (d.outcome || "").toUpperCase() === "YES" || (d.outcome || "").toUpperCase() === "UP"
                        ? "text-[#00C97D] dark:text-[#00FF9D]"
                        : "text-[#FF1744]";
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          {d.traderLabel || cleanTitle.replace(/^.*-\s*/, "")}
                        </p>
                        <p className="text-[12px] text-gray-500 dark:text-gray-400 truncate leading-snug">
                          {action}
                          {d.marketTitle ? (
                            <>
                              {" · "}
                              <span className="text-gray-700 dark:text-gray-300">{shortNotifTitle(d)}</span>
                            </>
                          ) : null}
                          {d.outcome ? (
                            <>
                              {" "}
                              <span className={outcomeColor}>{d.outcome}</span>
                            </>
                          ) : null}
                        </p>
                        {(d.tradeAmount && d.tradePrice != null) ? (
                          <p className="text-[11px] text-gray-500 dark:text-gray-300 tabular-nums mt-0.5">
                            {d.side === "SELL" || d.eventType === "closed" ? "Sold" : "Bought"}{" "}
                            {Number(d.tradeAmount).toFixed(2)} @ ${Number(d.tradePrice).toFixed(3)}
                            {d.calculatedValue != null
                              ? ` · $${Number(d.calculatedValue).toFixed(2)}`
                              : ""}
                          </p>
                        ) : null}
                      </>
                    );
                  } else if (notification.type === "copy_trade") {
                    const d = notification.data;
                    const sideColor = d.side === "BUY" ? "text-[#00FF9D]" : "text-[#FF1744]";
                    // `copy_trade` covers Trade Copied (success), Trade Failed,
                    // and the AutoPause / AutoResume variants — `notification.title`
                    // discriminates. We only render the structured "Copied X
                    // SIDE / size @ price" body for the success case; the
                    // others get a clearer header + the secondary message
                    // line below so the row doesn't read as a wall of
                    // stripped-emoji garbage.
                    const isFailure = (notification.title || '').toLowerCase().includes('failed') || typeof (d as any).error === 'string';
                    const isAutoPause = (notification.title || '').toLowerCase().includes('auto-paused');
                    const isAutoResume = (notification.title || '').toLowerCase().includes('resumed');

                    if (isFailure) {
                      body = (
                        <>
                          <div className="flex items-center gap-1.5 min-w-0 leading-snug">
                            <span className="text-[13px] font-medium text-gray-900 dark:text-white flex-shrink-0">Copy Failed</span>
                            <LeaderViaBadge name={d.leaderName} image={d.leaderImage || d.traderImageUrl} address={d.traderWalletAddress || d.leaderName} />
                          </div>
                          {d.marketTitle ? (
                            <p className="text-[12px] text-gray-500 dark:text-gray-400 line-clamp-2 leading-snug">
                              {shortNotifTitle(d)}
                            </p>
                          ) : null}
                          {(d as any).error ? (
                            <p className="text-[11px] text-[#FF1744] mt-0.5 line-clamp-1">
                              {String((d as any).error)}
                            </p>
                          ) : null}
                        </>
                      );
                    } else if (isAutoResume) {
                      // Same hero-metric pattern as PositionResolvedToast:
                      // line 1 = "Copy Resumed · @leader", line 2 = bold
                      // green metric ($balance or N/M positions) followed
                      // by the short reason. The SSE payload carries the
                      // numbers in `data.context` for these — see
                      // notification-service.ts:emitAutoResumed*. Falls
                      // back to text-only when the context isn't present.
                      const ctx = (d as any).context || {};
                      const isBalanceResume = (d as any).reason === 'balance_recovered'
                        || typeof ctx.balance === 'number';
                      const heroValue = isBalanceResume
                        ? (typeof ctx.balance === 'number' ? `$${ctx.balance.toFixed(2)}` : null)
                        : (typeof ctx.openCount === 'number' && typeof ctx.cap === 'number'
                            ? `${ctx.openCount}/${ctx.cap}` : null);
                      const heroLabel = isBalanceResume ? 'balance recovered' : 'positions open';
                      body = (
                        <>
                          <div className="flex items-center gap-1.5 min-w-0 leading-snug">
                            <span className="text-[13px] font-medium text-gray-900 dark:text-white flex-shrink-0">Copy Resumed</span>
                            <LeaderViaBadge name={d.leaderName} image={d.leaderImage || d.traderImageUrl} address={d.traderWalletAddress || d.leaderName} />
                          </div>
                          <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                            {heroValue ? (
                              <span className="font-monument text-[#00FF9D]">
                                {heroValue}
                              </span>
                            ) : null}
                            <span className="text-gray-500 dark:text-gray-300">{heroLabel}</span>
                          </p>
                        </>
                      );
                    } else if (isAutoPause) {
                      // Mirror the AutoResume hero-metric layout — line 1
                      // "Copy Paused · via @leader", line 2 bold red metric
                      // (loss %, balance, N/M positions, etc.) followed by
                      // a short label. The SSE payload's `data.context`
                      // carries the numbers (or backend-populated fields
                      // like reason/balance/openCount/cap). Falls back to
                      // the cleaned TG message when context isn't present
                      // so old payloads still render something useful.
                      const ctx = (d as any).context || {};
                      const reason = (d as any).reason || '';
                      let heroValue: string | null = null;
                      let heroLabel = '';
                      if (reason === 'loss_threshold') {
                        if (typeof ctx.currentLossPct === 'number') {
                          heroValue = `${ctx.currentLossPct > 0 ? '-' : ''}${Math.abs(ctx.currentLossPct).toFixed(1)}%`;
                        }
                        heroLabel = typeof ctx.lossCapPct === 'number'
                          ? `vs -${Math.abs(ctx.lossCapPct).toFixed(0)}% cap`
                          : 'loss threshold hit';
                      } else if (reason === 'insufficient_balance') {
                        if (typeof ctx.balance === 'number') heroValue = `$${ctx.balance.toFixed(2)}`;
                        heroLabel = typeof ctx.requiredUsd === 'number'
                          ? `needs $${ctx.requiredUsd.toFixed(2)}`
                          : 'insufficient balance';
                      } else if (reason === 'max_open_positions') {
                        if (typeof ctx.openCount === 'number' && typeof ctx.cap === 'number') {
                          heroValue = `${ctx.openCount}/${ctx.cap}`;
                        }
                        heroLabel = 'positions cap reached';
                      } else if (reason === 'below_min_size_consistent') {
                        if (typeof ctx.attemptedSizeUsd === 'number') heroValue = `$${ctx.attemptedSizeUsd.toFixed(2)}`;
                        heroLabel = typeof ctx.minSizeUsd === 'number'
                          ? `below $${ctx.minSizeUsd.toFixed(0)} minimum`
                          : 'below $1 minimum';
                      } else if (reason === 'above_max_size_consistent') {
                        if (typeof ctx.attemptedSizeUsd === 'number') heroValue = `$${ctx.attemptedSizeUsd.toFixed(0)}`;
                        heroLabel = typeof ctx.maxSizeUsd === 'number'
                          ? `above $${ctx.maxSizeUsd.toFixed(0)} cap`
                          : 'above your max cap';
                      }
                      const hasStructured = Boolean(heroValue) || Boolean(heroLabel);
                      body = (
                        <>
                          <div className="flex items-center gap-1.5 min-w-0 leading-snug">
                            <span className="text-[13px] font-medium text-gray-900 dark:text-white flex-shrink-0">Copy Paused</span>
                            <LeaderViaBadge name={d.leaderName} image={d.leaderImage || d.traderImageUrl} address={d.traderWalletAddress || d.leaderName} />
                          </div>
                          {hasStructured ? (
                            <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                              {heroValue ? (
                                <span className="font-monument text-[#FF1744]">
                                  {heroValue}
                                </span>
                              ) : null}
                              <span className="text-gray-500 dark:text-gray-300">{heroLabel}</span>
                            </p>
                          ) : notification.message ? (
                            <p className="text-[12px] text-gray-500 dark:text-gray-400 line-clamp-2 leading-snug mt-0.5">
                              {stripEmojiAndTags(notification.message).substring(0, 140)}
                            </p>
                          ) : null}
                        </>
                      );
                    } else {
                      // Successful copy_trade — matches the AutoPause / AutoResume /
                      // Skips family: line 1 = "Copy Trade · via @leader" + BUY/SELL
                      // pill, line 2 = hero $notional + size@price label, line 3 =
                      // market · outcome. Same "via @" idiom + hero metric makes a
                      // mixed feed read as one product instead of five.
                      const notionalNum = d.notional != null ? Number(d.notional) : null;
                      const sizeNum = d.size != null ? Number(d.size) : null;
                      const priceNum = d.price != null ? Number(d.price) : null;
                      const heroValue = notionalNum != null ? `$${notionalNum.toFixed(2)}` : null;
                      const sharesLabel = sizeNum != null ? sizeNum.toFixed(1).replace(/\.0$/, "") : "";
                      const heroLabel = sizeNum != null && priceNum != null
                        ? `${sharesLabel} @ ${formatCents(priceNum, 2)}${d.isLimitOrder ? " · limit" : ""}`
                        : (d.isLimitOrder ? "limit order" : "");
                      // Outcome chip lives on line 1 next to BUY/SELL so "what was
                      // bought" reads at a glance; line 3 keeps the market name.
                      // For multi-outcome PM markets prefer eventTitle + groupItemTitle
                      // (chip already carries the variant); else marketTitle + outcome.
                      const useEventVariant = d.groupItemTitle && d.eventTitle;
                      const outcomeHeadline = useEventVariant ? d.eventTitle : shortNotifTitle(d);
                      const outcomeChip = useEventVariant ? d.groupItemTitle : (d.groupItemTitle || d.outcome);
                      body = (
                        <>
                          <div className="flex items-center gap-1.5 min-w-0 leading-snug">
                            <span className="text-[13px] font-medium text-gray-900 dark:text-white flex-shrink-0">Copy Trade</span>
                            {d.side ? (
                              <span className={`px-1 py-px rounded-[3px] text-[10px] font-semibold flex-shrink-0 ${
                                d.side === "BUY"
                                  ? "bg-[#00FF9D]/20 text-[#00FF9D]"
                                  : "bg-[#FF1744]/20 text-[#FF1744]"
                              }`}>
                                {d.side}
                              </span>
                            ) : null}
                            {/* Outcome chip on line 1, right of BUY/SELL. `min-w-0
                                truncate` lets a long outcome shrink instead of
                                pushing the notional price off the row — the price
                                stays flex-shrink-0 + ml-auto so it's never covered. */}
                            {outcomeChip ? (
                              <span className="min-w-0 truncate inline-flex items-center rounded-[3px] bg-gray-100 dark:bg-[#25252B] px-1.5 py-px text-[10px] font-semibold text-gray-800 dark:text-gray-100">{outcomeChip}</span>
                            ) : null}
                            {heroValue ? (
                              <span className={`text-[13px] font-monument tabular-nums flex-shrink-0 ml-auto pl-1.5 ${sideColor}`}>{heroValue}</span>
                            ) : null}
                          </div>
                          {/* Line 2: via @leader · size@price · time (outcome moved to line 1) */}
                          <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                            <LeaderViaBadge name={d.leaderName} image={d.leaderImage || d.traderImageUrl} address={d.traderWalletAddress || d.leaderName} />
                            {heroLabel ? (
                              <span className="text-[11px] tabular-nums text-gray-500 dark:text-gray-300 truncate">{heroLabel}</span>
                            ) : null}
                            <span className="ml-auto flex-shrink-0 text-[10px] tabular-nums text-gray-400 dark:text-gray-500">{formatTimeAgo(notification.createdAt)}</span>
                          </div>
                          {outcomeHeadline ? (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-snug">{outcomeHeadline}</p>
                          ) : null}
                        </>
                      );
                    }
                  } else if (notification.type === "position_resolved") {
                    // Structured layout for resolved positions so the row
                    // doesn't render as a wall of stripped emoji + "Position
                    // Won Market: ... Outcome: ... You won! P&L: +$X
                    // Auto-redeemed to pUSD" on a single line.
                    const d = notification.data;
                    const won = d.won === true;
                    const pnl = typeof d.pnl === 'number' ? d.pnl : null;
                    // Drive color + headline + status off PnL sign, not the
                    // outcome-side `won` flag. A YES holder of a YES-resolved
                    // market with partial-sells / avg entry near $1 can net
                    // negative — pre-fix that row rendered green "$-22.88"
                    // and a triumphant "Position won" header. See
                    // notification-service.ts for the matching backend logic.
                    const PNL_EPS = 0.01;
                    const netPositive = pnl != null && pnl > PNL_EPS;
                    const netNegative = pnl != null && pnl < -PNL_EPS;
                    const pnlColor = netPositive
                      ? "text-[#00FF9D]"
                      : netNegative
                      ? "text-[#FF1744]"
                      : "text-gray-600 dark:text-gray-400";
                    const headline = netPositive
                      ? "Position won"
                      : netNegative
                      ? (won ? "Position closed" : "Position lost")
                      : "Position closed";
                    // Status label: only celebrate auto-redeem when net was
                    // positive. On net loss, suppress the "Auto-redeemed to
                    // pUSD" line so the row doesn't contradict the red PnL.
                    const statusLabel = !won
                      ? "Lost"
                      : netPositive
                      ? ((d as any).autoRedeemed === true ? "Auto-redeemed to pUSD" : "Ready to claim")
                      : "Closed";
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          {headline}
                          {d.outcome ? <span className="text-gray-500 dark:text-gray-400"> · {d.outcome}</span> : null}
                        </p>
                        {d.marketTitle ? (
                          <p className="text-[12px] text-gray-500 dark:text-gray-400 line-clamp-2 leading-snug">
                            {shortNotifTitle(d)}
                          </p>
                        ) : null}
                        <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                          {pnl != null ? (
                            <span className={`font-monument ${pnlColor}`}>
                              {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
                            </span>
                          ) : null}
                          <span className="text-gray-500 dark:text-gray-300">{statusLabel}</span>
                        </p>
                      </>
                    );
                  } else if (
                    notification.type === "copy_below_min" ||
                    notification.type === "copy_above_max" ||
                    notification.type === "insufficient_balance" ||
                    notification.type === "market_odds_too_high" ||
                    notification.type === "high_slippage"
                  ) {
                    // Copytrading "skip" alerts (gated 1/24h by Redis on the
                    // backend). Renders in the same hero-metric family as
                    // AutoPause / AutoResume — bold amber number + gray
                    // label below "Copy skipped · via @leader". Falls back
                    // to the legacy amber sentence when no numeric context
                    // is on the row, so older payloads keep working.
                    const d = notification.data;
                    let heroValue: string | null = null;
                    let heroLabel = '';
                    if (notification.type === "copy_below_min") {
                      if (typeof d.attemptedSizeUsd === 'number') heroValue = `$${d.attemptedSizeUsd.toFixed(2)}`;
                      heroLabel = typeof d.minSizeUsd === 'number'
                        ? `below $${d.minSizeUsd.toFixed(0)} minimum`
                        : 'below $1 minimum';
                    } else if (notification.type === "copy_above_max") {
                      if (typeof d.attemptedSizeUsd === 'number') heroValue = `$${d.attemptedSizeUsd.toFixed(0)}`;
                      heroLabel = typeof d.maxCopySizeUsd === 'number'
                        ? `above $${d.maxCopySizeUsd.toFixed(0)} cap`
                        : 'above your max cap';
                    } else if (notification.type === "insufficient_balance") {
                      if (typeof d.balance === 'number') heroValue = `$${d.balance.toFixed(2)}`;
                      heroLabel = typeof d.requiredUsd === 'number'
                        ? `needs $${d.requiredUsd.toFixed(2)}`
                        : 'not enough pUSD';
                    } else if (notification.type === "market_odds_too_high") {
                      if (typeof d.priceAsPct === 'number') heroValue = `${d.priceAsPct.toFixed(0)}%`;
                      heroLabel = typeof d.oddsCapPct === 'number'
                        ? `cap ${d.oddsCapPct.toFixed(0)}%`
                        : 'above your Max Odds';
                    } else if (notification.type === "high_slippage") {
                      if (typeof d.slippagePct === 'number') heroValue = `${d.slippagePct.toFixed(1)}%`;
                      heroLabel = typeof d.slippageCapPct === 'number'
                        ? `cap ${d.slippageCapPct.toFixed(1)}%`
                        : 'slippage cap exceeded';
                    }
                    const hasStructured = Boolean(heroValue) || Boolean(heroLabel);
                    const leaderHandle = d.leaderLabel || d.leaderName;
                    body = (
                      <>
                        <div className="flex items-center gap-1.5 min-w-0 leading-snug">
                          <span className="text-[13px] font-medium text-gray-900 dark:text-white flex-shrink-0">Copy skipped</span>
                          <LeaderViaBadge name={leaderHandle} image={d.leaderImage || d.traderImageUrl} address={d.traderWalletAddress || leaderHandle} />
                        </div>
                        {hasStructured ? (
                          <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                            {heroValue ? (
                              <span className="font-monument text-amber-500 dark:text-amber-400">
                                {heroValue}
                              </span>
                            ) : null}
                            <span className="text-gray-500 dark:text-gray-300">{heroLabel}</span>
                          </p>
                        ) : null}
                        {d.marketTitle ? (
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-snug">
                            {shortNotifTitle(d)}
                          </p>
                        ) : null}
                      </>
                    );
                  } else if (notification.type === "price_alert") {
                    // Price alert (watchlist target crossed). Backend:
                    //   { marketTitle, outcome, targetPrice, currentPrice,
                    //     direction: 'above' | 'below', marketSlug, eventSlug }
                    // The hero line is the cents value coloured by
                    // direction (above → emerald, below → red) with the
                    // "crossed above 80¢ target" detail below. Matches
                    // the dev preview structure exactly.
                    const d = notification.data || {};
                    const current = Number(d.currentPrice ?? 0);
                    const target = Number(d.targetPrice ?? 0);
                    const direction = String(d.direction || "above");
                    const outcome = String(d.outcome || "").toUpperCase();
                    const priceColor = direction === "above"
                      ? "text-[#00FF9D]"
                      : "text-[#FF1744]";
                    const formatCent = (v: number) => `${Math.round(v * 100)}¢`;
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          Price alert
                          {outcome ? (
                            <span className="text-gray-500 dark:text-gray-400"> · {outcome}</span>
                          ) : null}
                        </p>
                        <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                          <span className={`font-monument ${priceColor}`}>{formatCent(current)}</span>
                          <span className="text-gray-500 dark:text-gray-300">
                            crossed {direction} {formatCent(target)} target
                          </span>
                        </p>
                        {d.marketTitle ? (
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-snug">
                            {shortNotifTitle(d)}
                          </p>
                        ) : null}
                      </>
                    );
                  } else if (notification.type === "deposit_wrapped") {
                    // Auto-wrap success. Backend stamps:
                    //   { asset, amountUsd, route, feeUsd, ... }
                    // The user already has the funds tradeable; the row's
                    // job is to surface the amount + which path the
                    // deposit took (free Onramp vs Uniswap with fee) so a
                    // small balance discrepancy isn't a mystery later.
                    const d = notification.data || {};
                    const asset = String(d.asset || "USDC");
                    const amount = Number(d.amountUsd ?? 0);
                    const route = String(d.route || "onramp");
                    const fee = Number(d.feeUsd ?? 0);
                    const detail = route === "uniswap" && fee > 0
                      ? `${asset} → pUSD · uniswap · $${fee.toFixed(2)} fee`
                      : `${asset} → pUSD · free onramp`;
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          Deposit ready
                        </p>
                        <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                          <span className="font-monument text-[#00FF9D]">
                            +${amount.toFixed(2)}
                          </span>
                          <span className="text-gray-500 dark:text-gray-300">{detail}</span>
                        </p>
                      </>
                    );
                  } else if (notification.type === "deposit_needs_approval") {
                    // Funds arrived but per-asset CollateralOnramp
                    // approval is missing. Amber — pending user action.
                    // Backend: { asset, amountUsd, depositTxHash }.
                    const d = notification.data || {};
                    const asset = String(d.asset || "USDC");
                    const amount = Number(d.amountUsd ?? 0);
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          Deposit needs approval
                        </p>
                        <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                          <span className="font-monument text-amber-500 dark:text-amber-400">
                            ${amount.toFixed(2)}
                          </span>
                          <span className="text-gray-500 dark:text-gray-300">
                            {asset} — 1 gasless signature pending
                          </span>
                        </p>
                      </>
                    );
                  } else if (notification.type === "low_balance") {
                    // Balance crossed below the user's alert threshold.
                    // Backend: { balance, threshold }.
                    const d = notification.data || {};
                    const balance = Number(d.balance ?? 0);
                    const threshold = Number(d.threshold ?? 10);
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          Low balance
                        </p>
                        <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                          <span className="font-monument text-amber-500 dark:text-amber-400">
                            ${balance.toFixed(2)}
                          </span>
                          <span className="text-gray-500 dark:text-gray-300">
                            below ${threshold.toFixed(2)} threshold
                          </span>
                        </p>
                      </>
                    );
                  } else if (notification.type === "bridge_completed") {
                    // Inbound bridge settled. Backend:
                    //   { amount, sourceChain, destinationChain }
                    const d = notification.data || {};
                    const amount = Number(d.amount ?? 0);
                    const src = String(d.sourceChain || "");
                    const dst = String(d.destinationChain || "Polygon");
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          Bridge complete
                        </p>
                        <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                          <span className="font-monument text-[#00FF9D]">
                            +${amount.toFixed(2)}
                          </span>
                          <span className="text-gray-500 dark:text-gray-300">
                            {src ? `${src} → ${dst}` : `Arrived on ${dst}`}
                          </span>
                        </p>
                      </>
                    );
                  } else if (notification.type === "redeem_complete") {
                    // Manual / batch claim outcome from
                    // notification-service.sendRedeemComplete. Two
                    // backend variants:
                    //   totalFailed === 0 → "Claim complete" (emerald)
                    //   totalFailed > 0   → "Claim partial"  (amber)
                    // The structured body mirrors the dev preview so the
                    // bell row reads as "+$X · N positions · status" at
                    // a glance instead of the raw stripped TG message.
                    const d = notification.data || {};
                    const totalRedeemed = Number(d.totalRedeemed ?? 0);
                    const totalFailed = Number(d.totalFailed ?? 0);
                    const totalProfit = Number(d.totalProfit ?? 0);
                    const partial = totalFailed > 0;
                    const accent = partial
                      ? "text-amber-500 dark:text-amber-400"
                      : "text-[#00FF9D]";
                    const detail = partial
                      ? `${totalRedeemed} ok · ${totalFailed} failed`
                      : `${totalRedeemed} position${totalRedeemed === 1 ? "" : "s"} · auto to pUSD`;
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          {partial ? "Claim partial" : "Claim complete"}
                        </p>
                        <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                          <span className={`font-monument ${accent}`}>
                            +${totalProfit.toFixed(2)}
                          </span>
                          <span className="text-gray-500 dark:text-gray-300">{detail}</span>
                        </p>
                      </>
                    );
                  } else if (
                    notification.type === "limit_cancelled" ||
                    notification.type === "limit_filled" ||
                    notification.type === "limit_expired"
                  ) {
                    // Limit order lifecycle. Mirrors the structured layout
                    // we ship for copy/position events so the row reads as
                    // <verb chip> → <notional · size @ cents> → <market>
                    // instead of a stripped TG sentence. For Filled the
                    // notional is the actual dollar amount executed —
                    // bolded in the verb's colour so the user catches the
                    // size at a glance. Expired / Cancelled don't get
                    // notional (no fill occurred) but keep the size@cents
                    // line so the user remembers which order it was.
                    const d = notification.data;
                    const size = Number(d.size ?? 0);
                    const price = Number(d.price ?? 0);
                    const sizeOk = Number.isFinite(size) && size > 0;
                    const priceOk = Number.isFinite(price) && price > 0;
                    const notional = sizeOk && priceOk ? size * price : null;
                    const isFilled = notification.type === "limit_filled";
                    const isCancelled = notification.type === "limit_cancelled";
                    const verbColor = isFilled
                      ? "text-[#00C97D] dark:text-[#00FF9D]"
                      : isCancelled
                      ? "text-[#FF1744]"
                      : "text-amber-500 dark:text-amber-400";
                    const verb = isFilled
                      ? "Limit filled"
                      : isCancelled
                      ? "Limit cancelled"
                      : "Limit expired";
                    const chipBg = d.side === "BUY"
                      ? "bg-[#00FF9D]/20 text-[#00FF9D]"
                      : d.side === "SELL"
                      ? "bg-[#FF1744]/20 text-[#FF1744]"
                      : "bg-gray-500/10 text-gray-600 dark:text-gray-400";
                    const trailingDetail = !isFilled
                      ? (isCancelled
                          ? (typeof d.reason === 'string' && d.reason ? d.reason : null)
                          : "never reached")
                      : null;
                    const priceCents = priceOk ? `${Math.round(price * 100)}¢` : null;
                    // Always 1 decimal so the row reads uniformly even
                    // for integer sizes — matches the dev preview's
                    // "50.0 @ 72¢" target.
                    const sizeStr = sizeOk ? size.toFixed(1) : null;
                    body = (
                      <>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate leading-snug">
                          <span className={verbColor}>{verb}</span>
                          {d.side ? (
                            // Chip only on the success state (Filled).
                            // Expired / Cancelled never executed — the
                            // side is just context, so render it as
                            // muted text to keep the visual hierarchy
                            // (event > intent).
                            isFilled ? (
                              <span className={`ml-1.5 px-1 py-px rounded-[3px] text-[10px] font-semibold align-middle ${chipBg}`}>
                                {d.side}
                              </span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-400"> · {d.side}</span>
                            )
                          ) : null}
                        </p>
                        {(notional != null || (sizeStr && priceCents)) ? (
                          <p className="text-[11px] tabular-nums mt-0.5 flex items-center gap-1.5">
                            {notional != null ? (
                              <span className={`font-monument ${verbColor}`}>
                                ${notional.toFixed(2)}
                              </span>
                            ) : null}
                            {sizeStr && priceCents ? (
                              <span className="text-gray-500 dark:text-gray-300">
                                {sizeStr} @ {priceCents}
                                {trailingDetail ? ` · ${trailingDetail}` : ""}
                              </span>
                            ) : trailingDetail ? (
                              <span className="text-gray-500 dark:text-gray-300">{trailingDetail}</span>
                            ) : null}
                          </p>
                        ) : null}
                        {d.marketTitle ? (
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-snug">
                            {shortNotifTitle(d)}
                          </p>
                        ) : null}
                      </>
                    );
                  } else {
                    body = (
                      <>
                        <p
                          className={`text-[13px] leading-snug truncate ${
                            !notification.read
                              ? "font-medium text-gray-900 dark:text-white"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {cleanTitle}
                        </p>
                        {notification.message ? (
                          <p className="text-[12px] text-gray-500 dark:text-gray-400 line-clamp-2 leading-snug mt-0.5">
                            {stripEmojiAndTags(notification.message).substring(0, 110)}
                          </p>
                        ) : null}
                      </>
                    );
                  }

                  const content = (
                    <div
                      className={`flex items-start gap-3 p-3 rounded-[5px] border transition-colors cursor-pointer ${
                        !notification.read
                          ? "bg-white dark:bg-[#1f1f29] border-gray-200 dark:border-[#2a2a33] hover:bg-gray-50 dark:hover:bg-[#24242e]"
                          : "bg-white dark:bg-[#18181D] border-gray-100 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#1c1c22]"
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <NotificationThumb n={notification} />

                      <div className="flex-1 min-w-0">
                        {body}
                        {/* copy_trade carries its own time on line 2 (right); other
                            types keep the bottom-right footer time. */}
                        {notification.type !== "copy_trade" ? (
                          <div className="flex items-center justify-end gap-1.5 mt-1.5 text-[10px] text-gray-400 dark:text-gray-500">
                            <span>{formatTimeAgo(notification.createdAt)}</span>
                          </div>
                        ) : null}
                      </div>

                      {!notification.read ? (
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#CCFF00] animate-pulse mt-1.5" />
                      ) : null}
                    </div>
                  );

                  return link ? (
                    <Link
                      key={notification.id}
                      href={link}
                      onClick={() => {
                        handleNotificationClick(notification);
                        if (link.startsWith("/market/") || link.startsWith("/event/") || link.startsWith("/polymarket/event/") || link.startsWith("/polymarket/market/")) {
                          setNavigationSource("notification");
                        }
                      }}
                    >
                      {content}
                    </Link>
                  ) : (
                    <div key={notification.id}>{content}</div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-[#25252B]">
              <Link
                href="/settings?tab=notifications"
                prefetch={false}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {t("notifications.settingsLink")}
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
